import axios from 'axios';
import { authService } from './auth';
import studentAuthService from './studentAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple simultaneous logout calls
let isLoggingOut = false;

// Helper function to handle logout based on user type
const handleLogout = () => {
  // Prevent multiple logout calls
  if (isLoggingOut) return;
  isLoggingOut = true;

  const userType = localStorage.getItem('userType');

  if (userType === 'student') {
    studentAuthService.logout();
    // Only redirect if not already on student login page
    if (!window.location.pathname.includes('/student/login')) {
      window.location.href = '/student/login';
    }
  } else if (userType === 'admin') {
    authService.logout();
    // authService.logout() handles its own redirect
  } else {
    // Unknown or no userType - clear everything and go to main login
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('studentAuthToken');
    localStorage.removeItem('studentUser');
    localStorage.removeItem('mustChangePassword');
    localStorage.removeItem('userType');
    window.location.href = '/login';
  }

  // Reset flag after redirect (this may not execute due to redirect)
  setTimeout(() => {
    isLoggingOut = false;
  }, 100);
};

// Add token to request headers
api.interceptors.request.use(
  (config) => {
    // Check userType to determine which token to use
    const userType = localStorage.getItem('userType');
    const token = userType === 'student'
      ? localStorage.getItem('studentAuthToken')
      : localStorage.getItem('authToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      handleLogout();
      return Promise.reject(error);
    }

    // Handle network errors (like CORS) - treat as authentication issue
    if (!error.response && error.code === 'ERR_NETWORK') {
      handleLogout();
      return Promise.reject(error);
    }

    // Handle 403 Forbidden errors
    if (error.response?.status === 403) {
      handleLogout();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);


// Student API calls
export const studentAPI = {
  getAll: (skip = 0, limit = 100, activeOnly = true, batchId = null) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      active_only: activeOnly.toString(),
      sort_by: 'first_name',
      sort_order: 'asc'
    });
    if (batchId) {
      params.append('batch_id', batchId);
    }
    return api.get(`/api/students/?${params.toString()}`);
  },
  getById: (id) => api.get(`/api/students/${id}`),
  create: (data) => api.post('/api/students/', data),
  update: (id, data) => api.put(`/api/students/${id}`, data),
  delete: (id) => api.delete(`/api/students/${id}`),
  deactivate: (id) => api.put(`/api/students/${id}/deactivate`),
  activate: (id) => api.put(`/api/students/${id}/activate`),
  search: (searchTerm) => {
    const params = new URLSearchParams({
      sort_by: 'first_name',
      sort_order: 'asc'
    });
    return api.get(`/api/students/search/${searchTerm}?${params.toString()}`);
  },
  getByEmail: (email) => api.get(`/api/students/email/${email}`),
  getEnrollments: (studentId) => api.get(`/api/students/${studentId}/enrollments`),
  getFees: (studentId) => api.get(`/api/students/${studentId}/fees`),
  getCount: () => api.get('/api/students/count/total'),
  // Student Portal Management
  createPortalAccess: (studentId) => api.post(`/api/students/${studentId}/create-portal-access`),
  revokePortalAccess: (studentId) => api.delete(`/api/students/${studentId}/revoke-portal-access`),
};

// Batch API calls
export const batchAPI = {
  getAll: (skip = 0, limit = 100, activeOnly = true) => 
    api.get(`/api/batches/?skip=${skip}&limit=${limit}&active_only=${activeOnly}`),
  getById: (id) => api.get(`/api/batches/${id}`),
  create: (data) => api.post('/api/batches/', data),
  update: (id, data) => api.put(`/api/batches/${id}`, data),
  delete: (id) => api.delete(`/api/batches/${id}`),
  search: (searchTerm) => api.get(`/api/batches/search/${searchTerm}`),
  getBySubject: (subject) => api.get(`/api/batches/subject/${subject}`),
  getByInstructor: (instructor) => api.get(`/api/batches/instructor/${instructor}`),
  getCount: () => api.get('/api/batches/count/total'),
};

// Enrollment API calls
export const enrollmentAPI = {
  getAll: (skip = 0, limit = 100, activeOnly = true) => 
    api.get(`/api/enrollments/?skip=${skip}&limit=${limit}&active_only=${activeOnly}`),
  getById: (id) => api.get(`/api/enrollments/${id}`),
  create: (data) => api.post('/api/enrollments/', data),
  update: (id, data) => api.put(`/api/enrollments/${id}`, data),
  delete: (id) => api.delete(`/api/enrollments/${id}`),
  search: (searchTerm) => api.get(`/api/enrollments/search/${searchTerm}`),
  getByStudent: (studentId) => api.get(`/api/enrollments/student/${studentId}`),
  getByBatch: (batchId) => api.get(`/api/enrollments/batch/${batchId}`),
  markEnrollmentFeePaid: (enrollmentId) => api.post(`/api/enrollments/${enrollmentId}/pay-enrollment-fee`),
  getCount: () => api.get('/api/enrollments/count/total'),
};

// Fee API calls
export const feeAPI = {
  getAll: (skip = 0, limit = 100, activeOnly = true, search = null) => {
    let url = `/api/fees/?skip=${skip}&limit=${limit}&active_only=${activeOnly}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    return api.get(url);
  },
  getByStudent: (studentId) => api.get(`/api/fees/student/${studentId}`),
  getById: (id) => api.get(`/api/fees/${id}`),
  create: (data) => api.post('/api/fees/', data),
  update: (id, data) => api.put(`/api/fees/${id}`, data),
  delete: (id) => api.delete(`/api/fees/${id}`),
  markAsPaid: (id) => api.patch(`/api/fees/${id}/pay`),
  getFeeStructures: (studentId) => api.get(`/api/fees/structures/${studentId}`),
  createPayment: (data) => api.post('/api/fees/payments/', data),
  getPayments: (feeStructureId) => api.get(`/api/fees/payments/${feeStructureId}`),
  getInstallments: (feeStructureId) => api.get(`/api/fees/${feeStructureId}/installments`),
  updateInstallmentStatus: (installmentId) => api.patch(`/api/fees/installments/${installmentId}/status`),
  generateInvoice: (paymentId, sendEmail = true) => api.post(`/api/fees/payments/${paymentId}/invoice?send_email=${sendEmail}`),
  sendReminder: (feeStructureId, reminderData) => api.post(`/api/fees/reminder/${feeStructureId}`, reminderData),
};

// Dashboard API calls
export const dashboardAPI = {
  getStats: () => api.get('/api/dashboard/stats'),
  getBirthdaysToday: () => api.get('/api/dashboard/birthdays/today'),
};

// Payment API calls
export const paymentAPI = {
  getAll: (skip = 0, limit = 100) => 
    api.get(`/api/fees/payments/?skip=${skip}&limit=${limit}`),
  getById: (id) => api.get(`/api/fees/payments/payment/${id}`),
  create: (data) => api.post('/api/fees/payments/', data),
  getByFeeStructure: (feeStructureId) => api.get(`/api/fees/payments/${feeStructureId}`),
  getByStudent: (studentId) => api.get(`/api/fees/payments/student/${studentId}`),
  getByDateRange: (startDate, endDate) => 
    api.get(`/api/fees/payments/date-range/?start_date=${startDate}&end_date=${endDate}`),
};

// Activity API calls
export const activityAPI = {
  getRecent: (limit = 10) => api.get(`/api/activities/?limit=${limit}`),
  getDashboard: (limit = 5) => api.get(`/api/activities/dashboard?limit=${limit}`),
  getByType: (activityType, limit = 50) => api.get(`/api/activities/type/${activityType}?limit=${limit}`),
  getByStudent: (studentId, limit = 20) => api.get(`/api/activities/student/${studentId}?limit=${limit}`),
  getByBatch: (batchId, limit = 20) => api.get(`/api/activities/batch/${batchId}?limit=${limit}`),
};

// Invitation API calls
export const invitationAPI = {
  getAll: (skip = 0, limit = 100, includeExpired = false) => {
    console.log('📡 InvitationAPI.getAll called with:', { skip, limit, includeExpired });
    const url = `/api/invitations/?skip=${skip}&limit=${limit}&include_expired=${includeExpired}`;
    console.log('📡 InvitationAPI.getAll URL:', url);
    return api.get(url);
  },
  getById: (id) => api.get(`/api/invitations/${id}`),
  create: (data) => api.post('/api/invitations/', data),
  delete: (id) => api.delete(`/api/invitations/${id}`),
  resend: (id) => api.post(`/api/invitations/${id}/resend`),
  getStats: () => api.get('/api/invitations/stats'),
  getMyInvitations: (skip = 0, limit = 100) => 
    api.get(`/api/invitations/my-invitations?skip=${skip}&limit=${limit}`),
  getByToken: (token) => {
    console.log('🔍 invitationAPI.getByToken called with token:', token);
    return api.get(`/api/invitations/token/${token}`);
  },
  accept: (data) => api.post('/api/invitations/accept', data),
};

// Enquiry API calls
export const enquiryAPI = {
  getAll: (page = 1, size = 10, status = null, source = null, search = null) => {
    const params = new URLSearchParams({
      page,
      size,
      ...(status && { status }),
      ...(source && { source }),
      ...(search && { search })
    });
    return api.get(`/api/enquiries/?${params}`);
  },
  getById: (id) => api.get(`/api/enquiries/${id}`),
  create: (data) => api.post('/api/enquiries/', data),
  update: (id, data) => api.put(`/api/enquiries/${id}`, data),
  delete: (id) => api.delete(`/api/enquiries/${id}`),
  updateStatus: (id, status, contactedBy = null) => 
    api.patch(`/api/enquiries/${id}/status`, { status, contacted_by: contactedBy }),
  getStats: () => api.get('/api/enquiries/stats/summary/'),
  generateQRCode: () => api.post('/api/enquiries/qr-code/'),
  validateQRCode: (qrCodeId) => api.get(`/api/enquiries/qr-code/${qrCodeId}/validate/`),
  submitQRCodeEnquiry: (qrCodeId, data) => api.post(`/api/enquiries/qr-code/${qrCodeId}/submit/`, data),
};

// Workshop API calls
export const workshopAPI = {
  getAll: (skip = 0, limit = 300, activeOnly = true, search = null) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      active_only: activeOnly.toString(),
      ...(search && { search })
    });
    return api.get(`/api/workshops/?${params.toString()}`);
  },
  getById: (id) => api.get(`/api/workshops/${id}`),
  create: (data) => api.post('/api/workshops/', data),
  update: (id, data) => api.put(`/api/workshops/${id}`, data),
  delete: (id) => api.delete(`/api/workshops/${id}`),
  getRegistrations: (workshopId, skip = 0, limit = 300) => 
    api.get(`/api/workshops/${workshopId}/registrations?skip=${skip}&limit=${limit}`),
  verifyPayment: (registrationId) => api.patch(`/api/workshops/registrations/${registrationId}/verify`),
  deleteRegistration: (registrationId) => api.delete(`/api/workshops/registrations/${registrationId}`),
  getPaymentScreenshot: (registrationId) => {
    return api.get(`/api/workshops/registrations/${registrationId}/payment-screenshot`, {
      responseType: 'blob',
    });
  },
  // Attendance API calls
  generateAttendanceQR: (workshopId, data) => api.post(`/api/workshops/${workshopId}/attendance/qr`, data),
  getAttendanceQR: (workshopId) => api.get(`/api/workshops/${workshopId}/attendance-qr`),
  markAttendance: (qrCodeId, data) => api.post(`/api/workshops/attendance/${qrCodeId}/mark`, data),
  getAttendance: (workshopId, skip = 0, limit = 300) => 
    api.get(`/api/workshops/${workshopId}/attendance?skip=${skip}&limit=${limit}`),
};

// Public Workshop API calls (no authentication required)
export const publicWorkshopAPI = {
  getByPublicLink: (publicLinkId) => api.get(`/api/workshops/public/${publicLinkId}`),
  register: (publicLinkId, data) => api.post(`/api/workshops/public/${publicLinkId}/register`, data),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

// Task API calls
export const taskAPI = {
  getAll: (skip = 0, limit = 100, filters = {}) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      ...(filters.status && { status: filters.status }),
      ...(filters.priority && { priority: filters.priority }),
      ...(filters.category && { category: filters.category }),
      ...(filters.assigned_to && { assigned_to: filters.assigned_to }),
      ...(filters.created_by && { created_by: filters.created_by }),
      ...(filters.tags && { tags: filters.tags.join(',') }),
      ...(filters.due_date_from && { due_date_from: filters.due_date_from }),
      ...(filters.due_date_to && { due_date_to: filters.due_date_to }),
      ...(filters.search && { search: filters.search })
    });
    return api.get(`/api/tasks/?${params.toString()}`);
  },
  getById: (id) => api.get(`/api/tasks/${id}`),
  create: (data) => api.post('/api/tasks/', data),
  update: (id, data) => api.put(`/api/tasks/${id}`, data),
  delete: (id) => api.delete(`/api/tasks/${id}`),
  getKanban: () => api.get('/api/tasks/kanban'),
  getStats: () => api.get('/api/tasks/stats'),
  updateStatus: (id, status) => api.patch(`/api/tasks/${id}/status`, { status }),
  addComment: (id, comment) => api.post(`/api/tasks/${id}/comments`, { comment }),
  getMyTasks: (skip = 0, limit = 100) => 
    api.get(`/api/tasks/my-tasks?skip=${skip}&limit=${limit}`),
};

// Organization API calls
export const organizationAPI = {
  create: (data) => api.post('/api/organizations/', data),
  getAll: (skip = 0, limit = 100, status = null) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      ...(status && { status })
    });
    return api.get(`/api/organizations/?${params.toString()}`);
  },
  getById: (id) => api.get(`/api/organizations/${id}`),
  getBySlug: (slug) => api.get(`/api/organizations/slug/${slug}`),
  update: (id, data) => api.put(`/api/organizations/${id}`, data),
  delete: (id) => api.delete(`/api/organizations/${id}`),
  getStats: (id) => api.get(`/api/organizations/${id}/stats`),
};

// Subscription API calls
export const subscriptionAPI = {
  getStatus: () => api.get('/api/subscriptions/status'),
  getPricingPlans: () => api.get('/api/subscriptions/plans'),
  checkQuota: (moduleName) => api.get(`/api/subscriptions/quota/${moduleName}`),
  upgradePlan: (planId) => api.post(`/api/subscriptions/upgrade/${planId}`),
  getUsage: () => api.get('/api/subscriptions/usage'),
};

// Profile Picture API calls
export const profilePictureAPI = {
  upload: (data) => api.post('/api/auth/profile-picture', data),
  get: () => {
    return api.get('/api/auth/profile-picture', {
      responseType: 'blob',
    });
  },
  delete: () => api.delete('/api/auth/profile-picture'),
};

// Employee API calls
export const employeeAPI = {
  getAll: (skip = 0, limit = 100, activeOnly = true, department = null) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      active_only: activeOnly.toString(),
      sort_by: 'first_name',
      sort_order: 'asc'
    });
    if (department) {
      params.append('department', department);
    }
    return api.get(`/api/employees/?${params.toString()}`);
  },
  getById: (id) => api.get(`/api/employees/${id}`),
  create: (data) => api.post('/api/employees/', data),
  update: (id, data) => api.put(`/api/employees/${id}`, data),
  delete: (id) => api.delete(`/api/employees/${id}`),
  search: (searchTerm) => {
    const params = new URLSearchParams({
      sort_by: 'first_name',
      sort_order: 'asc'
    });
    return api.get(`/api/employees/search/${searchTerm}?${params.toString()}`);
  },
  getByEmail: (email) => api.get(`/api/employees/email/${email}`),
  getActiveCount: () => api.get('/api/employees/count/active'),
};

// Payroll API calls
export const payrollAPI = {
  generate: (data) => api.post('/api/payroll/generate', data),
  getAll: (month, year, skip = 0, limit = 100, status = null) => {
    const params = new URLSearchParams({
      month: month.toString(),
      year: year.toString(),
      skip: skip.toString(),
      limit: limit.toString(),
    });
    if (status) params.append('status', status);
    return api.get(`/api/payroll/?${params.toString()}`);
  },
  getById: (payrollId) => api.get(`/api/payroll/${payrollId}`),
  getByEmployee: (employeeId, skip = 0, limit = 12) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    return api.get(`/api/payroll/employee/${employeeId}?${params.toString()}`);
  },
  getSummary: (month, year) => api.get(`/api/payroll/summary/${month}/${year}`),
  getRun: (month, year) => api.get(`/api/payroll/run/${month}/${year}`),
  finalize: (payrollId, data = {}) => api.patch(`/api/payroll/${payrollId}/finalize`, data),
  finalizeAll: (month, year) => api.post(`/api/payroll/finalize-all/${month}/${year}`),
  markAsPaid: (payrollId, data) => api.patch(`/api/payroll/${payrollId}/pay`, data),
  bulkPay: (data) => api.post('/api/payroll/bulk-pay', data),
  sendSalarySlip: (payrollId) => api.post(`/api/payroll/${payrollId}/salary-slip`),
  delete: (payrollId) => api.delete(`/api/payroll/${payrollId}`),
  deleteMonthly: (month, year) => api.delete(`/api/payroll/month/${month}/${year}`),
  updateRemarks: (payrollId, remarks) => api.patch(`/api/payroll/${payrollId}/remarks?remarks=${encodeURIComponent(remarks)}`),
  update: (payrollId, data) => api.patch(`/api/payroll/${payrollId}`, data),
};

// Employee Attendance API calls
export const employeeAttendanceAPI = {
  // Admin endpoints
  getOrCreateQR: () => api.get('/api/employee-attendance/qr'),
  deactivateQR: () => api.delete('/api/employee-attendance/qr'),
  getAttendanceRecords: (skip = 0, limit = 100, startDate = null, endDate = null, employeeId = null) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (employeeId) params.append('employee_id', employeeId);
    return api.get(`/api/employee-attendance/?${params.toString()}`);
  },

  // Public endpoints (no auth required)
  validateQR: (qrCodeId) => api.get(`/api/employee-attendance/validate-qr/${qrCodeId}`),
  getStatus: (qrCodeId, phone) => api.get(`/api/employee-attendance/status/${qrCodeId}?phone=${phone}`),
  checkIn: (qrCodeId, data) => api.post(`/api/employee-attendance/check-in/${qrCodeId}`, data),
  checkOut: (qrCodeId, data) => api.post(`/api/employee-attendance/check-out/${qrCodeId}`, data),
};

export const studentAttendanceAPI = {
  // Admin endpoints
  getOrCreateQR: () => api.get('/api/student-attendance/qr'),
  deactivateQR: () => api.delete('/api/student-attendance/qr'),
  getAttendanceRecords: (skip = 0, limit = 100, startDate = null, endDate = null, studentId = null) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (studentId) params.append('student_id', studentId);
    return api.get(`/api/student-attendance/?${params.toString()}`);
  },

  // Public endpoints (no auth required)
  validateQR: (qrCodeId) => api.get(`/api/student-attendance/validate-qr/${qrCodeId}`),
  getStatus: (qrCodeId, phone) => api.get(`/api/student-attendance/status/${qrCodeId}?phone=${phone}`),
  checkIn: (qrCodeId, data) => api.post(`/api/student-attendance/check-in/${qrCodeId}`, data),
  checkOut: (qrCodeId, data) => api.post(`/api/student-attendance/check-out/${qrCodeId}`, data),
};

// Test API calls
export const testAPI = {
  // Admin endpoints
  create: (data) => api.post('/api/tests/', data),
  getAll: (skip = 0, limit = 100, isActive = null) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    if (isActive !== null) params.append('is_active', isActive.toString());
    return api.get(`/api/tests/?${params.toString()}`);
  },
  getById: (testId) => api.get(`/api/tests/${testId}`),
  update: (testId, data) => api.patch(`/api/tests/${testId}`, data),
  delete: (testId) => api.delete(`/api/tests/${testId}`),
  getLeaderboard: (testId, limit = 100) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    return api.get(`/api/tests/${testId}/leaderboard?${params.toString()}`);
  },
  getAttempts: (testId, skip = 0, limit = 100) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    return api.get(`/api/tests/${testId}/attempts?${params.toString()}`);
  },
  getTopPerformers: (testId = null) => {
    const params = testId ? new URLSearchParams({ test_id: testId }) : '';
    return api.get(`/api/tests/top-performers${params ? '?' + params.toString() : ''}`);
  },

  // Public endpoints (no auth required)
  getPublicTest: (testId) => api.get(`/api/tests/public/${testId}`),
  startTest: (testId, data) => api.post(`/api/tests/public/${testId}/start`, data),
  submitTest: (testId, data) => api.post(`/api/tests/public/${testId}/submit`, data),
  getPublicLeaderboard: (testId, limit = 100) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    return api.get(`/api/tests/public/${testId}/leaderboard?${params.toString()}`);
  },
};

// Chat API calls (Student Portal only)
export const chatAPI = {
  // Student endpoints
  getStudentConversations: (limit = 50, skip = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), skip: skip.toString() });
    return api.get(`/api/chat/student/conversations?${params.toString()}`);
  },
  createStudentConversation: (participantId, participantType) => {
    return api.post('/api/chat/student/conversations', {
      participant_id: participantId,
      participant_type: participantType
    });
  },
  getPresenceStatus: (userIds) => {
    const params = new URLSearchParams({ user_ids: userIds.join(',') });
    return api.get(`/api/chat/student/presence?${params.toString()}`);
  },
};

// AI Chat API calls (Student Portal)
export const aiChatAPI = {
  // Send a message to AI and get response
  sendMessage: (conversationId, message, model = null) => {
    return api.post('/api/student-auth/ai-chat/message', {
      conversation_id: conversationId,
      message: message,
      model: model
    });
  },

  // Get list of AI conversations
  getConversations: (limit = 20, skip = 0) => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      skip: skip.toString()
    });
    return api.get(`/api/student-auth/ai-chat/conversations?${params.toString()}`);
  },

  // Get a specific conversation with messages
  getConversation: (conversationId) => {
    return api.get(`/api/student-auth/ai-chat/conversations/${conversationId}`);
  },

  // Delete a conversation
  deleteConversation: (conversationId) => {
    return api.delete(`/api/student-auth/ai-chat/conversations/${conversationId}`);
  },

  // Get available AI models
  getAvailableModels: () => {
    return api.get('/api/student-auth/ai-chat/models');
  }
};

// Practice Mode API calls (Student Portal)
export const practiceAPI = {
  // Generate new practice questions
  generateQuestions: (config) => {
    return api.post('/api/student-auth/practice/generate', config);
  },

  // Get a practice session by ID (optionally navigate to a specific question)
  getSession: (sessionId, questionIndex = null) => {
    const params = questionIndex !== null ? `?question_index=${questionIndex}` : '';
    return api.get(`/api/student-auth/practice/sessions/${sessionId}${params}`);
  },

  // Submit an answer for a question
  submitAnswer: (sessionId, questionIndex, answer, timeSpentSeconds = 0) => {
    return api.post(`/api/student-auth/practice/sessions/${sessionId}/answer`, {
      question_index: questionIndex,
      answer: answer,
      time_spent_seconds: timeSpentSeconds
    });
  },

  // Get a hint for a question
  getHint: (sessionId, questionIndex, userCode = null) => {
    return api.post(`/api/student-auth/practice/sessions/${sessionId}/hint`, {
      question_index: questionIndex,
      user_code: userCode
    });
  },

  // Submit/complete a practice session
  submitSession: (sessionId) => {
    return api.post(`/api/student-auth/practice/sessions/${sessionId}/submit`);
  },

  // Get practice history
  getHistory: (limit = 10, skip = 0) => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      skip: skip.toString()
    });
    return api.get(`/api/student-auth/practice/history?${params.toString()}`);
  },

  // Get overall practice progress
  getProgress: () => {
    return api.get('/api/student-auth/practice/progress');
  },

  // Get weak topics for focused practice
  getWeakTopics: () => {
    return api.get('/api/student-auth/practice/weak-topics');
  }
};

export default api;

